import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
// =====================================================================
// TODO: Replace YOUR_FORM_ID with your actual Formspree form ID
// 1. Sign up at https://formspree.io
// 2. Create a new form, verify your email
// 3. Copy the form ID (looks like 'xyzabcde' or 'mldwbpra')
// 4. Paste it below
// =====================================================================
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
const ACCENT = '#3B4A4F';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    // Honeypot spam check — if the hidden field is filled, it's a bot
    if (formData.get('_gotcha')) {
      setStatus('success'); // pretend success to fool bots
      return;
    }
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data?.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2401" data-mp="2grdsext4czdl">
      {/* HERO */}
      <section aria-hidden="true" className="relative min-h-[426px] md:h-[426px] bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/ciMoYgyZJfoJ97A3ycBnwG/ChatGPT_Image_Sep_1,_2026,_12_29_37_AM.png')"
    }} data-id="element-2402" data-mp="2gpb6aov37qe5">
        {/* Angled shape that blends the hero into the page below it */}
        <img src="https://cdn.magicpatterns.com/uploads/5iWH92hTkHZtcmpNtDYfpW/path3.svg" alt="" className="absolute inset-x-0 bottom-0 w-full h-[48px] md:h-[72px] object-fill pointer-events-none" data-id="element-2403" data-mp="1xtujdu6nvgr6" />
      </section>

      {/* FORM SECTION */}
      <section className="bg-[#F9F9F9] py-20 md:py-28 px-6" data-id="element-2404" data-mp="1a49vqxzkbvbk">
        <div className="max-w-3xl mx-auto" data-id="element-2405" data-mp="1f1s68st8ailo">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} data-id="element-2406" data-mp="1f1ijde3hcrk7">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: '#3B4A4F'
          }} data-id="element-2407" data-mp="qji0uhqsel09">
              Send a Message
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-10" data-id="element-2408" data-mp="1eekqgrxf0shs">
              Tell us what you need help with.
            </h2>

            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white border-l-4 border-tealslate p-8 flex gap-5" data-id="element-2409" data-mp="1o4nuro9zlsvf">
                <CheckCircle2 className="w-8 h-8 text-tealslate shrink-0" data-id="element-2410" data-mp="1s7k7iycuiurg" />
                <div data-id="element-2411" data-mp="3mmzc2okd4c8w">
                  <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2" data-id="element-2412" data-mp="3mlobsyq4lmmj">
                    Message sent.
                  </h3>
                  <p className="text-[#3A3A33] leading-relaxed mb-4" data-id="element-2413" data-mp="3rjzmtcs5q36c">
                    Thanks for reaching out — we'll get back to you within 48
                    hours. Keep an eye on your inbox (including the spam folder,
                    just in case).
                  </p>
                  <button onClick={() => setStatus('idle')} className="text-[11px] font-bold text-tealslate uppercase tracking-[0.2em] hover:text-[#2A3A3F] transition-colors inline-flex items-center gap-2" data-id="element-2414" data-mp="33pg31kxwmtmh">
                    Send another message <ArrowRight className="w-3 h-3" data-id="element-2415" data-mp="3hofrarhi96i4" />
                  </button>
                </div>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2416" data-mp="s0oszsfgrjdf">
                {/* Honeypot — hidden, bots will fill it */}
                <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px'
            }} aria-hidden="true" data-id="element-2417" data-mp="171vo56wxbs1l" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2418" data-mp="ob4iafc5ajry">
                  <div data-id="element-2419" data-mp="jbrotbqrqege">
                    <label htmlFor="name" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2420" data-mp="jbi525wbh3j5">
                      Full Name *
                    </label>
                    <input id="name" name="name" type="text" required className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2421" data-mp="adfxt8lzpenf" />
                  </div>
                  <div data-id="element-2422" data-mp="6v8r666m0a53">
                    <label htmlFor="email" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2423" data-mp="3p7eynb53pld7">
                      Email *
                    </label>
                    <input id="email" name="email" type="email" required className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2424" data-mp="ktghvwd1yf7t" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2425" data-mp="2wkki2pben650">
                  <div data-id="element-2426" data-mp="31j5iopukjrtz">
                    <label htmlFor="phone" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2427" data-mp="31fhodohav1e7">
                      Phone{' '}
                      <span className="text-[#5A5A50] font-normal normal-case tracking-normal" data-id="element-2428" data-mp="32yhn6eglajzd">
                        (optional)
                      </span>
                    </label>
                    <input id="phone" name="phone" type="tel" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2429" data-mp="2aa2bo1wo2jss" />
                  </div>
                  <div data-id="element-2430" data-mp="2f7kjfoyy6sz7">
                    <label htmlFor="interest" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2431" data-mp="2f7c6jx2trno0">
                      I'm Interested In *
                    </label>
                    <select id="interest" name="interest" required defaultValue="" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2432" data-mp="112q07xxuai5h">
                      <option value="" disabled data-id="element-2433" data-mp="1jz9cgw3xwm69">
                        Select one…
                      </option>
                      <option value="Order Status" data-id="element-2434" data-mp="1oxj8ta1ehstg">Order status</option>
                      <option value="Return or Exchange" data-id="element-2435" data-mp="1tqvjshd5q13q">
                        Return or exchange
                      </option>
                      <option value="Damaged or Missing Item" data-id="element-2436" data-mp="1yo5hekap9j2n">
                        Damaged or missing item
                      </option>
                      <option value="Product Question" data-id="element-2437" data-mp="27pbzgxszz1y0">
                        Product or ingredient question
                      </option>
                      <option value="Book Download Issue" data-id="element-2438" data-mp="3s8flvhe8szd3">
                        Book download issue
                      </option>
                      <option value="Wholesale" data-id="element-2439" data-mp="3o8f3jlo0w40r">Wholesale &amp; bulk</option>
                      <option value="Other" data-id="element-2440" data-mp="3j77xps5fxewq">Something else</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-2441" data-mp="fpzilg23kt5p">
                  <label htmlFor="message" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2442" data-mp="fqip80marv4f">
                    Message *
                  </label>
                  <textarea id="message" name="message" required rows={6} placeholder="Include your order number if you have one…" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-2443" data-mp="5ycw2nkb77nn" />
                </div>

                {status === 'error' && <div className="bg-red-50 border-l-4 border-red-600 p-4 flex gap-3 items-start" data-id="element-2444" data-mp="p0msymkkuvvi">
                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-2445" data-mp="k1jlywl1efkw" />
                    <p className="text-sm text-red-800" data-id="element-2446" data-mp="k3mo2ezgrz5z">{errorMessage}</p>
                  </div>}

                <div className="pt-2" data-id="element-2447" data-mp="t282pz1teu1h">
                  <button type="submit" disabled={status === 'submitting'} className="px-10 py-4 bg-tealslate text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#2A3A3F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-3" data-id="element-2448" data-mp="t16r9tgw05j8">
                    {status === 'submitting' ? <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2449" data-mp="1coe4hnyan4vq" />
                        Sending…
                      </> : <>
                        Send Message <ArrowRight className="w-4 h-4" data-id="element-2450" data-mp="1memfguhfp1oi" />
                      </>}
                  </button>
                </div>

                <p className="text-xs text-[#5A5A50] italic pt-4" data-id="element-2451" data-mp="1rf0n4bf4i9m0">
                  Your information stays private. We respond personally to every
                  message.
                </p>
              </form>}
          </motion.div>
        </div>
      </section>
    </div>;
}